import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { CheckCircle, LocalFireDepartment, Schedule, DirectionsRun } from '@mui/icons-material';
import { CLIENT_PLAY_WORKOUTS } from 'api/graphql/playCoach';
import { muscleLabel } from 'components/play/constants';
import { formatDuration } from 'components/play/workoutEstimate';

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), "EEEE d 'de' MMMM", { locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play (coach) — historial de entrenamientos del cliente con su estado REAL:
 * verde completado / ámbar abandonado con su % de series, duración y kcal.
 */
function PlayClientWorkoutsList({ clientId }) {
  const { t } = useTranslation(['play']);
  const { data, loading } = useQuery(CLIENT_PLAY_WORKOUTS, {
    variables: { user_id: clientId, first: 30 },
    fetchPolicy: 'cache-and-network'
  });

  const workouts = data?.clientPlayWorkouts || [];

  if (loading && workouts.length === 0) {
    return (
      <Stack spacing={1.25}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '14px' }} />
        ))}
      </Stack>
    );
  }

  if (workouts.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'hairline.main', borderRadius: 3, color: 'text.secondary' }}>
        <Typography variant="body2" fontWeight={600}>
          {t('coach.workoutsEmpty', { ns: 'play', defaultValue: 'Este cliente aún no registra entrenamientos.' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {workouts.map((w) => {
        const ok = w.status === 'completed';
        return (
          <Stack
            key={w.id}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              p: 1.75,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.content'
            }}>
            <CheckCircle
              sx={{ fontSize: 22, flex: 'none', color: ok ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg' }}
            />
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800} noWrap>
                {w.muscle_focus ? muscleLabel(t, w.muscle_focus) : t('home.lastWorkout.generic', { ns: 'play' })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {fmtDate(w.completed_at || w.started_at || w.created_at)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 'none', color: 'text.secondary' }}>
              <Stack direction="row" spacing={0.4} alignItems="center">
                <Schedule sx={{ fontSize: 14, color: 'gold.main' }} />
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                  {formatDuration(w.total_seconds)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.4} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <DirectionsRun sx={{ fontSize: 14, color: 'gold.main' }} />
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                  {t('home.lastWorkout.sets', { ns: 'play', count: w.total_sets || 0 })}
                </Typography>
              </Stack>
              {w.estimated_calories > 0 && (
                <Stack direction="row" spacing={0.4} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                  <LocalFireDepartment sx={{ fontSize: 14, color: 'gold.main' }} />
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {w.estimated_calories}
                  </Typography>
                </Stack>
              )}
              {w.completion_percent !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    color: ok ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg',
                    backgroundColor: ok ? 'sessionStatus.completed.bg' : 'sessionStatus.pending.bg'
                  }}>
                  {w.completion_percent}%
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

PlayClientWorkoutsList.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default PlayClientWorkoutsList;
